import { useEffect, useState } from "react";
import { Scale } from "lucide-react";
import { appApi, inr, fmtDate } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";

const FILTERS = ["pending_review", "deducted", "rejected", "all"];

export default function AdminWeight() {
  const [filter, setFilter] = useState("pending_review");
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    appApi.get("admin/weight-discrepancies", { status_filter: filter })
      .then(setData)
      .catch(() => setData({ orders: [], total: 0 }));
  }, [filter]);

  const orders = data?.orders || [];

  return (
    <div data-testid="admin-weight-page" className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-mono font-bold uppercase tracking-[0.2em] text-blaze">Admin</p>
          <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl mt-1">Weight Discrepancies</h1>
          <p className="text-sm text-zinc-500 mt-2">Courier re-weighs across all merchants — merchants approve or reject from their panel.</p>
        </div>
        {data && (
          <span className="font-mono text-xs font-bold uppercase tracking-widest bg-obsidian text-sky-300 px-4 py-2" data-testid="weight-total">
            {data.total} in view
          </span>
        )}
      </div>

      <div className="flex gap-0 border-2 border-obsidian w-fit bg-white flex-wrap" data-testid="weight-filter-tabs">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`weight-tab-${f}`}
            className={`px-5 py-2.5 text-xs font-mono font-bold uppercase tracking-widest transition-colors duration-150 ${filter === f ? "bg-obsidian text-sky-300" : "hover:bg-acid/40"}`}>
            {f.replace(/_/g, " ")}
          </button>
        ))}
      </div>

      <div className="border-2 border-obsidian bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]" data-testid="weight-table">
          <thead>
            <tr className="border-b-2 border-obsidian text-left">
              {["AWB", "Merchant", "Courier", "Route", "Declared", "Charged", "Extra", "Status", "Created"].map((h) => (
                <th key={h} className="px-4 py-3 text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => {
              const w = o.weight_discrepancy || {};
              return (
                <tr key={o.id || o.awb} className="border-b border-zinc-100 last:border-0 hover:bg-acid/20 transition-colors duration-100" data-testid={`weight-row-${o.awb}`}>
                  <td className="px-4 py-3 font-mono text-xs font-bold">{o.awb}</td>
                  <td className="px-4 py-3 text-xs text-zinc-600">{o.merchant_email || o.merchant_name || o.user_email || "—"}</td>
                  <td className="px-4 py-3 text-zinc-600">{o.courier_name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-500">{o.pickup?.pincode} → {o.delivery?.pincode}</td>
                  <td className="px-4 py-3 font-mono text-xs">{w.declared_weight_kg ?? o.package?.weight_kg ?? "—"}kg</td>
                  <td className="px-4 py-3 font-mono text-xs font-bold">{w.charged_weight_kg ?? w.courier_weight_kg ?? "—"}kg</td>
                  <td className="px-4 py-3 font-mono text-xs font-bold text-blaze">
                    {(w.extra_charge_inr ?? w.extra_amount_inr) != null ? inr(w.extra_charge_inr ?? w.extra_amount_inr) : "—"}
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={o.status} /></td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-500">{fmtDate(o.created_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {data && orders.length === 0 && (
          <div className="px-5 py-10 flex items-center gap-4" data-testid="weight-empty">
            <span className="w-10 h-10 bg-acid text-white flex items-center justify-center"><Scale className="w-5 h-5" /></span>
            <p className="text-sm text-zinc-500">No {filter.replace(/_/g, " ")} discrepancies right now.</p>
          </div>
        )}
        {!data && <p className="px-5 py-10 font-mono text-xs uppercase tracking-[0.3em] text-zinc-400">Loading…</p>}
      </div>
    </div>
  );
}
